import { Injectable, Injector, inject } from '@angular/core';
import { BebopUser } from 'src/app/interfaces/user';
import {
  Auth,
  signInWithEmailAndPassword,
  UserCredential,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut
} from 'firebase/auth';
import {
  Firestore,
  doc,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { FIREBASE_AUTH, FIREBASE_FIRESTORE } from '../firebase-config';
import { AppStateService } from './app-state.service';
import { AuthStatePersistenceService, type StoredAuthUser } from './auth-state-persistence.service';
import { SecureStorageService } from './secure-storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  public firestore = inject(FIREBASE_FIRESTORE);
  private auth = inject(FIREBASE_AUTH);
  private storage = inject(SecureStorageService);
  private authStatePersistence = inject(AuthStatePersistenceService);
  private appStateService = inject(AppStateService);
  private injector = inject(Injector);

  userData: StoredAuthUser | null = null;
  private isLoggingOut = false;

  get logoutInProgress(): boolean {
    return this.isLoggingOut;
  }

  constructor() {
    this.initAuthListener();
    this.suppressLogoutErrors();
  }

  private initAuthListener(): void {
    this.authStatePersistence.initializeAuthListener();
  }

  async storeUserLocally(user: StoredAuthUser | '' = ''): Promise<void> {
    await this.authStatePersistence.storeUserLocally(user);
  }

  // Login in with email/password
  SignIn(email: string, password: string): Promise<UserCredential> {
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  // Register user with email/password
  RegisterUser(email: string, password: string): Promise<UserCredential> {
    return createUserWithEmailAndPassword(this.auth, email, password);
  }

  // Email verification when new user register
  async SendVerificationMail(): Promise<void> {
    const user = this.auth.currentUser;
    if (user) {
      await sendEmailVerification(user);
      await this.appStateService.navigateTo('login');
    } else {
      throw new Error('No user is currently signed in.');
    }
  }

  // Recover password
  async PasswordRecover(passwordResetEmail: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.auth, passwordResetEmail);
      // window.alert('Password reset email has been sent, please check your inbox.');
    } catch(error) {
      // window.alert(error instanceof Error ? error.message : String(error));
    }
  }

  // Returns true when user is logged in
  get isLoggedIn(): boolean {
    // Note: Synchronous getter with async storage - will need migration strategy
    // For now, returns false and should be replaced with async method
    console.warn('isLoggedIn: Synchronous getter with async storage not supported. Use async method instead.');
    return false;
  }

  // Async version to check if user is logged in
  async isUserLoggedIn(): Promise<boolean> {
    const user = await this.storage.getObject<StoredAuthUser | 'null'>('user');
    return user !== null && user !== 'null' && user?.emailVerified !== false;
  }

  // Returns true when user's email is verified
  get isEmailVerified(): boolean {
    // Note: Synchronous getter with async storage - will need migration strategy
    console.warn('isEmailVerified: Synchronous getter with async storage not supported. Use async method instead.');
    return false;
  }

  // Async version to check if user's email is verified
  async isUserEmailVerified(): Promise<boolean> {
    const user = await this.storage.getObject<StoredAuthUser>('user');
    return user?.emailVerified !== false;
  }
  
  // Store user in localStorage
  async SetUserData(user: StoredAuthUser): Promise<void> {
    const userRef = doc(this.firestore, `users/${user.uid}`);
    const userData: BebopUser = {
      uid: user.uid,
      email: user.email ?? undefined,
      firstname: user.firstname,
      emailVerified: user.emailVerified,
    };

    try {
      await setDoc(userRef, userData, { merge: true });
    } catch (error) {
      console.error('Error setting user data:', error);
      throw error; // Re-throw the error if you want calling code to handle it
    }
  }

  // Suppress expected Firebase permission errors during logout
  private suppressLogoutErrors(): void {
    // Temporarily disabled: overriding console/error handlers causes conflicts with
    // some browser/devtools extensions (e.g., Arc devtools `sendElementToArc`).
  }


  // Sign-out
  async logoutUser(): Promise<void> {
    try {
      // Set flag to suppress expected permission errors
      this.isLoggingOut = true;
      
      // Clear user data first to prevent new subscriptions
      await this.storage.removeItem('user');
      
      // Sign out from Firebase (this will trigger permission errors on active listeners, which is expected)
      await signOut(this.auth);

      const publicSettings = await this.storage.getObject<{ darkmode?: boolean }>('public_settings');
      const useDarkMode = typeof publicSettings?.darkmode === 'boolean'
        ? publicSettings.darkmode
        : false;

      this.appStateService.applyDarkMode(useDarkMode);
      await this.appStateService.navigateTo('/public/account/login');
      
      // Reset flag after a brief delay to allow errors to be suppressed
      setTimeout(() => {
        this.isLoggingOut = false;
      }, 1000);
    } catch (error) {
      this.isLoggingOut = false;
      console.error('Logout error:', error);
    }
  }

  async logPwaLoginAttempt(username: string): Promise<void> {
    const attemptID = doc(this.firestore, 'pwa_login_attempts').id;
    const attemptRef = doc(this.firestore, `pwa_login_attempts/${attemptID}`);

    try {
      await setDoc(attemptRef, {
        username: username,
        timestamp: serverTimestamp(),
        user_agent: navigator.userAgent,
        platform: 'pwa'
      });
    } catch (error) {
      console.error('Error logging PWA login attempt:', error);
      throw error;
    }
  }
}

